import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ValidatorForm } from "components";
import {
  TextValidator,
  ValidatorForm as ValidatorFormClass,
} from "react-material-ui-form-validator";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Container,
  Link,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import QrCodeIcon from "@mui/icons-material/QrCode";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import { getEnv } from "../../envConfig";

const env = getEnv();

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      setError("Invalid or missing reset token");
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    // Add custom validation rule for password confirmation
    ValidatorFormClass.addValidationRule("isPasswordMatch", (value) => {
      return value === newPassword;
    });

    return () => {
      ValidatorFormClass.removeValidationRule("isPasswordMatch");
    };
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${env.ENDPOINT}/api/restaurant/auth/reset-password`,
        {
          token,
          newPassword,
        },
      );

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to reset password. The link may have expired.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  // Success State
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          py: 6,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Animated Background Icons */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.25,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <Box
            component={motion.div}
            animate={{
              y: [0, -25, 0],
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            sx={{
              position: "absolute",
              top: "15%",
              left: "10%",
              opacity: 0.2,
            }}
          >
            <QrCodeIcon sx={{ fontSize: 70, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 30, 0],
              rotate: [0, -15, 15, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            sx={{
              position: "absolute",
              top: "20%",
              right: "15%",
              opacity: 0.18,
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 68, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 25, 0],
              rotate: [0, 15, -15, 0],
            }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "8%",
              opacity: 0.2,
            }}
          >
            <LocalDiningIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, -28, 0],
              rotate: [0, 12, -12, 0],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.8,
            }}
            sx={{
              position: "absolute",
              bottom: "15%",
              right: "12%",
              opacity: 0.19,
            }}
          >
            <FastfoodIcon sx={{ fontSize: 66, color: "white" }} />
          </Box>
        </Box>

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            sx={{ textAlign: "center" }}
          >
            {/* Success Icon */}
            <MotionBox
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 3,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 60px rgba(255, 255, 255, 0.3)",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 80, color: "white" }} />
              </Box>
            </MotionBox>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
                color: "white",
                textShadow: "0 2px 20px rgba(0,0,0,0.2)",
              }}
            >
              Password Reset Successful
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "1.15rem",
                fontWeight: 500,
                mb: 4,
                textShadow: "0 1px 10px rgba(0,0,0,0.1)",
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Your password has been successfully reset. You can now login with
              your new password.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate("/login")}
              sx={{
                py: 2,
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: 2.5,
                background: "rgba(255, 255, 255, 0.98)",
                color: "#667eea",
                textTransform: "none",
                boxShadow: "0 12px 40px rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  background: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 48px rgba(255, 255, 255, 0.4)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              Go to Login
            </Button>
          </MotionBox>
        </Container>
      </Box>
    );
  }

  // Error State - Invalid Token
  if (error && !token) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1a0000 0%, #3d0000 50%, #2b0000 100%)"
              : "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
          py: 6,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Animated Background Icons */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.15,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <Box
            component={motion.div}
            animate={{
              y: [0, -25, 0],
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            sx={{
              position: "absolute",
              top: "15%",
              left: "10%",
              opacity: 0.1,
            }}
          >
            <QrCodeIcon sx={{ fontSize: 70, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 30, 0],
              rotate: [0, -15, 15, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            sx={{
              position: "absolute",
              top: "20%",
              right: "15%",
              opacity: 0.1,
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 68, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 25, 0],
              rotate: [0, 15, -15, 0],
            }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "8%",
              opacity: 0.1,
            }}
          >
            <LocalDiningIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, -28, 0],
              rotate: [0, 12, -12, 0],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.8,
            }}
            sx={{
              position: "absolute",
              bottom: "15%",
              right: "12%",
              opacity: 0.1,
            }}
          >
            <FastfoodIcon sx={{ fontSize: 66, color: "white" }} />
          </Box>
        </Box>

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            sx={{ textAlign: "center" }}
          >
            {/* Error Icon */}
            <MotionBox
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 3,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 80, color: "white" }} />
              </Box>
            </MotionBox>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
                color: "white",
                textShadow: "0 2px 20px rgba(0,0,0,0.2)",
              }}
            >
              Invalid Reset Link
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "1.15rem",
                fontWeight: 500,
                mb: 4,
                textShadow: "0 1px 10px rgba(0,0,0,0.1)",
                maxWidth: 500,
                mx: "auto",
              }}
            >
              {error}
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate("/forgot-password")}
              sx={{
                py: 2,
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: 2.5,
                background: "rgba(255, 255, 255, 0.98)",
                color: "#d32f2f",
                textTransform: "none",
                boxShadow: "0 12px 40px rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  background: "white",
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 48px rgba(255, 255, 255, 0.4)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              Request New Link
            </Button>
          </MotionBox>
        </Container>
      </Box>
    );
  }

  // Form State
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 6,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Animated Background Icons */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.25,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <Box
          component={motion.div}
          animate={{
            y: [0, -25, 0],
            rotate: [0, 10, -10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            top: "15%",
            left: "10%",
            opacity: 0.2,
          }}
        >
          <QrCodeIcon sx={{ fontSize: 70, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, 30, 0],
            rotate: [0, -15, 15, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          sx={{
            position: "absolute",
            top: "20%",
            right: "15%",
            opacity: 0.18,
          }}
        >
          <RestaurantMenuIcon sx={{ fontSize: 68, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, 25, 0],
            rotate: [0, 15, -15, 0],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5,
          }}
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "8%",
            opacity: 0.2,
          }}
        >
          <LocalDiningIcon sx={{ fontSize: 64, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, -28, 0],
            rotate: [0, 12, -12, 0],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.8,
          }}
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "12%",
            opacity: 0.19,
          }}
        >
          <FastfoodIcon sx={{ fontSize: 66, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, -18, 0],
            rotate: [0, -18, 18, 0],
          }}
          transition={{
            duration: 13,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.5,
          }}
          sx={{
            position: "absolute",
            top: "60%",
            right: "20%",
            opacity: 0.17,
          }}
        >
          <RamenDiningIcon sx={{ fontSize: 68, color: "white" }} />
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: "2rem", md: "2.75rem" },
                color: "white",
                textShadow: "0 2px 20px rgba(0,0,0,0.2)",
                letterSpacing: "-0.02em",
              }}
            >
              Reset Your Password
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "1.15rem",
                fontWeight: 500,
                textShadow: "0 1px 10px rgba(0,0,0,0.1)",
              }}
            >
              Enter your new password below
            </Typography>
          </Box>

          {/* Form Card */}
          <MotionCard
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              background:
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 40, 0.95)"
                  : "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(20px)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(255,255,255,0.5)",
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 5 } }}>
              <ValidatorForm
                onSubmit={handleSubmit}
                instantValidate={false}
                autoComplete="off"
              >
                <Box
                  sx={{
                    "& .MuiTextField-root": {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "& fieldset": {
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(0, 0, 0, 0.1)",
                          borderWidth: 1.5,
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.03)",
                          "& fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1.5,
                          },
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.02)",
                          boxShadow: `0 0 0 3px ${alpha(
                            theme.palette.primary.main,
                            0.1,
                          )}`,
                          "& fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                          },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        "&.Mui-focused": {
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.95rem",
                        padding: "16px 14px",
                      },
                    },
                  }}
                >
                  <TextValidator
                    label="New Password"
                    onChange={(e) => setNewPassword(e.target.value)}
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    validators={["required", "minStringLength:8"]}
                    errorMessages={[
                      "Password is required",
                      "Password must be at least 8 characters",
                    ]}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextValidator
                    label="Confirm Password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    validators={["required", "isPasswordMatch"]}
                    errorMessages={[
                      "Confirm password is required",
                      "Passwords do not match",
                    ]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Typography
                      color="error"
                      sx={{
                        mt: 2,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      mt: 3,
                      py: 1.8,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 32px ${alpha(
                          theme.palette.primary.main,
                          0.5,
                        )}`,
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </Box>
              </ValidatorForm>
            </CardContent>
          </MotionCard>

          {/* Footer */}
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "0.95rem",
                textShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            >
              Remember your password?{" "}
              <Link
                href="/login"
                sx={{
                  fontWeight: 700,
                  color: "white",
                  textDecoration: "none",
                  borderBottom: "2px solid rgba(255,255,255,0.5)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderBottomColor: "white",
                    opacity: 0.9,
                  },
                }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PasswordReset;
